import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SharePriceChart.css";

const HEIGHT = 170;
const MARGIN = { top: 10, right: 12, bottom: 22, left: 48 };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_MS = 24 * 60 * 60 * 1000;

// How far back the chart goes (from the latest price), and its x-axis ticks: month
// starts every tickMonths, or Mondays every tickDays. Ticks are twice as far apart
// when the chart is narrow.
const RANGES = [
  { key: "2Y", months: 24, period: "2 years", tickMonths: 6 },
  { key: "1Y", months: 12, period: "year", tickMonths: 3 },
  { key: "6M", months: 6, period: "6 months", tickMonths: 1 },
  { key: "1M", months: 1, period: "month", tickDays: 7 },
];
const NARROW_WIDTH = 260;

// Round a rough axis step up to 1, 2 or 5 x 10^n
const niceStep = (rough) => {
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
};

// About `count` evenly spaced round values covering min..max
const niceTicks = (min, max, count = 4) => {
  if (min === max) {
    const pad = Math.abs(min) * 0.05 || 1;
    min -= pad;
    max += pad;
  }
  const step = niceStep((max - min) / count);
  const ticks = [];
  for (let t = Math.floor(min / step) * step; t <= max + step / 2; t += step) {
    ticks.push(Number(t.toFixed(10)));
    if (t >= max) break;
  }
  return ticks;
};

const formatDate = (time) => {
  const d = new Date(time);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const formatTick = (value) => value.toLocaleString("en-GB", { maximumFractionDigits: 2 });

// Month starts (every `every` months) from firstTime to lastTime
const monthTicks = (firstTime, lastTime, every) => {
  const ticks = [];
  const start = new Date(firstTime);
  for (
    let year = start.getUTCFullYear(), month = start.getUTCMonth() + 1;
    Date.UTC(year, month, 1) <= lastTime;
    month += 1
  ) {
    if (month > 11) {
      year += Math.floor(month / 12);
      month %= 12;
    }
    if (month % every === 0) {
      ticks.push({ time: Date.UTC(year, month, 1), label: `${MONTHS[month]} ${String(year).slice(2)}` });
    }
  }
  return ticks;
};

// Mondays (every `every` days) from firstTime to lastTime
const dayTicks = (firstTime, lastTime, every) => {
  const ticks = [];
  const start = new Date(firstTime);
  const daysToMonday = (8 - start.getUTCDay()) % 7;
  let time = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + daysToMonday);
  for (; time <= lastTime; time += every * DAY_MS) {
    const d = new Date(time);
    ticks.push({ time, label: `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` });
  }
  return ticks;
};

// Daily share price line with a crosshair tooltip, and buttons to show the last 2
// years, year, 6 months or month. prices: [["YYYY-MM-DD", price], ...] in date order
// (up to 2 years); formatPrice turns a price into text with its currency; title goes
// on the left of the buttons.
const SharePriceChart = ({ prices, formatPrice, title }) => {
  const wrapperRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [rangeKey, setRangeKey] = useState("2Y");
  const range = RANGES.find((r) => r.key === rangeKey);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  const allPoints = useMemo(
    () => prices.map(([date, price]) => ({ time: Date.parse(date), price })),
    [prices],
  );

  // The points in the chosen range, counting back from the latest price
  const points = useMemo(() => {
    if (!allPoints.length) return allPoints;
    const last = new Date(allPoints[allPoints.length - 1].time);
    const cutoff = Date.UTC(last.getUTCFullYear(), last.getUTCMonth() - range.months, last.getUTCDate());
    return allPoints.filter((p) => p.time >= cutoff);
  }, [allPoints, range]);

  const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 0);
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

  const chart = useMemo(() => {
    if (points.length < 2 || !plotWidth) return null;
    const firstTime = points[0].time;
    const lastTime = points[points.length - 1].time;
    const priceValues = points.map((p) => p.price);
    const yTicks = niceTicks(Math.min(...priceValues), Math.max(...priceValues));
    const yMin = yTicks[0];
    const yMax = yTicks[yTicks.length - 1];

    const x = (time) => ((time - firstTime) / (lastTime - firstTime || 1)) * plotWidth;
    const y = (price) => plotHeight - ((price - yMin) / (yMax - yMin || 1)) * plotHeight;

    const spacing = plotWidth < NARROW_WIDTH ? 2 : 1;
    const xTicks = range.tickMonths
      ? monthTicks(firstTime, lastTime, range.tickMonths * spacing)
      : dayTicks(firstTime, lastTime, range.tickDays * spacing);

    const path = points
      .map((p, i) => `${i ? "L" : "M"}${x(p.time).toFixed(1)},${y(p.price).toFixed(1)}`)
      .join("");
    return { x, y, yTicks, xTicks, path };
  }, [points, plotWidth, plotHeight, range]);

  // Snap the crosshair to the trading day nearest the pointer
  const handleMove = (e) => {
    if (!chart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - MARGIN.left;
    let lo = 0;
    let hi = points.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (chart.x(points[mid].time) < px) lo = mid;
      else hi = mid;
    }
    const nearest = Math.abs(chart.x(points[lo].time) - px) <= Math.abs(chart.x(points[hi].time) - px) ? lo : hi;
    if (nearest !== hoverIndex) setHoverIndex(nearest);
  };

  const chooseRange = (key) => {
    setRangeKey(key);
    setHoverIndex(null);
  };

  const hover = chart && hoverIndex !== null ? points[hoverIndex] : null;
  const last = points[points.length - 1];

  return (
    <div className="share-price-block">
      <div className="share-price-header">
        <div className="share-price-title">{title}</div>
        <div className="share-price-ranges" role="group" aria-label="Chart range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={r.key === rangeKey ? "active" : ""}
              aria-pressed={r.key === rangeKey}
              onClick={() => chooseRange(r.key)}
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>
      <div className="share-price-chart" ref={wrapperRef}>
        {points.length < 2 ? (
          <p className="share-price-empty">No share price data for the last {range.period}.</p>
        ) : chart && (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={`Share price over the last ${range.period}, latest ${formatPrice(last.price)} on ${formatDate(last.time)}`}
            onMouseMove={handleMove}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {chart.yTicks.map((tick) => (
                <g key={tick} transform={`translate(0,${chart.y(tick)})`}>
                  <line className="grid" x2={plotWidth} />
                  <text className="axis-label" x={-6} dy="0.32em" textAnchor="end">
                    {formatTick(tick)}
                  </text>
                </g>
              ))}
              {chart.xTicks.map((tick) => (
                <text
                  key={tick.time}
                  className="axis-label"
                  x={chart.x(tick.time)}
                  y={plotHeight + 15}
                  textAnchor="middle"
                >
                  {tick.label}
                </text>
              ))}
              <path className="price-line" d={chart.path} />
              {hover && (
                <>
                  <line className="crosshair" x1={chart.x(hover.time)} x2={chart.x(hover.time)} y2={plotHeight} />
                  <circle className="hover-dot" cx={chart.x(hover.time)} cy={chart.y(hover.price)} r={4} />
                </>
              )}
              {/* Hit area: the whole plot, so the pointer never has to land on the line */}
              <rect width={plotWidth} height={plotHeight} fill="transparent" />
            </g>
          </svg>
        )}
        {hover && (
          <div
            className={`share-price-tooltip ${chart.x(hover.time) > plotWidth / 2 ? "left" : "right"}`}
            style={{ left: MARGIN.left + chart.x(hover.time), top: MARGIN.top }}
          >
            <div className="tooltip-date">{formatDate(hover.time)}</div>
            <div className="tooltip-value">{formatPrice(hover.price)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharePriceChart;
